import { Button, Stack, Typography } from '@mui/material'
import { ReactNode } from 'react'
import { ButtonProps, Icons } from 'utoniq-ui'

import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { CellFrame, Organism } from '@/core/architecture'
import { individualHiddenItemInfo } from '@/core/individual'

type Item = Pick<ButtonProps, 'onClick' | 'href' | 'sx' | 'disabled'> & {
  title?: string | number
  icon?: ReactNode
  color?: string
  hidden?: boolean
  active?: boolean
  activeColor?: string
  inactiveColor?: string
}

export type SupplementLinkAreaProps = {
  itemId?: string
  artistId: string
  transparent?: boolean
  startItems: Array<
    Item & {
      type:
        | 'expansion'
        | 'itemInfo'
        | 'serial'
        | 'trade'
        | 'share'
        | 'protect'
        | 'wallpaper'
    }
  >
  endItems: Array<Item & { type: 'comment' }>
  // analytics送信するためのページ名
  pageName: string
}

type ItemType =
  | 'expansion'
  | 'itemInfo'
  | 'serial'
  | 'trade'
  | 'share'
  | 'protect'
  | 'wallpaper'
  | 'comment'

export const SupplementLinkArea: Organism<SupplementLinkAreaProps> = ({
  startItems,
  endItems,
  itemId,
  artistId,
  pageName,
  transparent,
}) => {
  const { t } = useTranslation('ownedItemDetail')
  const { analytics } = useAnalytics()
  const { router, routerPages, generatePath } = useRouter()

  const defaultSupplement: Record<ItemType, Item & { type: ItemType }> = {
    expansion: {
      type: 'expansion',
      title: 'expansion',
      icon: <Icons.Expansion sx={{ mx: 'auto', color: 'grey.600' }} />,
      color: 'text.secondary',
      inactiveColor: 'text.disabled',
    },
    itemInfo: {
      hidden: individualHiddenItemInfo(),
      type: 'itemInfo',
      title: 'itemInfo',
      icon: <Icons.ItemInfo sx={{ mx: 'auto', color: 'grey.600' }} />,
      href: generatePath(routerPages.OwnedItemInformation, { itemId }),
      color: 'text.secondary',
      inactiveColor: 'text.disabled',
    },
    serial: {
      type: 'serial',
      title: 'serial',
      icon: <Icons.Serial sx={{ mx: 'auto', color: 'grey.600' }} />,
      color: 'text.secondary',
      inactiveColor: 'text.disabled',
    },
    trade: {
      type: 'trade',
      title: 'trade',
      icon: <Icons.TradeRequest sx={{ mx: 'auto', color: 'grey.600' }} />,
      color: 'text.secondary',
      activeColor: 'text.secondary',
      inactiveColor: 'text.disabled',
    },
    share: {
      type: 'share',
      title: 'share',
      icon: <Icons.Share sx={{ mx: 'auto', color: 'grey.600' }} />,
      color: 'text.secondary',
      inactiveColor: 'text.disabled',
    },
    protect: {
      type: 'protect',
      title: 'protect',
      icon: <Icons.Protect sx={{ mx: 'auto', color: 'grey.600' }} />,
      color: 'text.secondary',
      activeColor: 'primary.main',
      inactiveColor: 'text.disabled',
    },
    wallpaper: {
      type: 'wallpaper',
      title: 'wallpaper',
      icon: <Icons.Wallpaper sx={{ mx: 'auto', color: 'grey.600' }} />,
      color: 'text.secondary',
      inactiveColor: 'text.disabled',
    },
    comment: {
      type: 'comment',
      title: 'comment',
      icon: <Icons.Reply sx={{ color: 'grey.600' }} />,
      onClick() {
        analytics?.track('click_comment', {
          applicationKey: config.applicationKey,
          artistId,
          name: pageName,
        })
      },
      href: generatePath(routerPages.ItemComment, { itemId }),
      color: 'text.secondary',
      inactiveColor: 'text.disabled',
      sx: {
        alignSelf: 'stretch',
        display: 'flex',
        height: 'unset',
      },
    },
  }
  return (
    <CellFrame
      sx={{ bgcolor: transparent ? 'transparent' : 'background.default' }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ py: 2, px: 4, minHeight: 64 }}
      >
        {[startItems, endItems].map((items, index) => (
          <Stack
            direction="row"
            justifyContent="flex-start"
            key={index}
            alignSelf="stretch"
          >
            {items?.map(item => {
              const {
                type,
                sx,
                color,
                icon,
                title,
                hidden,
                active,
                activeColor,
                inactiveColor,
                ...props
              } = {
                ...defaultSupplement[item.type],
                ...item,
              }
              if (
                (!config.modules.trade || (!props.onClick && !props.href)) &&
                type === 'trade'
              )
                return
              if (!props.onClick && !props.href && type === 'wallpaper') return
              if (!props.onClick && !props.href && type === 'share') return
              if (!props.onClick && !props.href && type === 'expansion') return
              if (
                (!config.modules.trade || (!props.onClick && !props.href)) &&
                type === 'protect'
              )
                return
              if (hidden && type === 'itemInfo') return
              if (hidden && type === 'trade') return

              return (
                <Button
                  variant="text"
                  key={index}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    p: 1,
                    color:
                      active !== undefined
                        ? active
                          ? activeColor
                          : inactiveColor
                        : color,
                    borderRadius: 0,
                    textAlign: 'center',
                    minWidth: 44,
                    '&.Mui-disabled': {
                      background: 'inherit',
                      color: 'text.disabled',
                      '.MuiSvgIcon-root': {
                        color: 'inherit',
                      },
                    },
                    '.MuiSvgIcon-root, .MuiTypography-root':
                      active !== undefined && !active
                        ? { color: 'inherit' }
                        : undefined,
                    ...sx,
                  }}
                  {...props}
                >
                  {icon}
                  <Typography
                    variant={index ? 'body2' : 'overline'}
                    color={props.disabled ? 'text.disabled' : 'text.hint'}
                    sx={{ pt: 1 }}
                  >
                    {t((title ?? '') as string)}
                  </Typography>
                </Button>
              )
            })}
          </Stack>
        ))}
      </Stack>
    </CellFrame>
  )
}
